import React, {
  useMemo, useRef, useState, useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import CustomChartLegend from './CustomChartLegend';
import CustomChartToolTip from './CustomChartToolTip';
import { BoxShadow } from '../../../../shared/GlobalStyles';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import Text from '../../../../shared/_TBComponents/Text';

const ChartWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  position: relative;
  border: solid 1px #E0E0E0;
  border-radius: 8px;
  margin-bottom: 30px;
  padding-bottom: 12px;
`;

const ExerciseHeader = styled('div')`
  display: flex;
  background: #fcfcfc;
  padding: 14px 16px;
  border-bottom: solid 1px #E0E0E0;
  border-radius: 8px 8px 0 0;
  min-height: 56px;
`;

const FilterWrapper = styled('div')`
  display: flex;
  justify-content: center;
  filter: ${(props) => (props.isBlurred ? 'blur(1.2px)' : null)};
`;

const PrintWrapper = styled('div')`
  @media print {
    @page {
      size: A4 landscape;
      margin: 6px;
    }
    transform: scale(0.8);
    transform-origin: top left;
    width: 2500px;
    max-height: unset;

    @media (min-width: 1440px) {
      transform: scale(0.75);
    }

    @media (min-width: 1920px) {
      transform: scale(0.5);
    }
  }
`;

const ChartContainer = styled('div')`
  flex: 1;
  flex-grow: 1;
  min-width: 0; 
  height: ${(props) => (props.isLoading ? '355px' : '90%')};
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  filter: ${(props) => (props.isBlurred ? 'blur(1.2px)' : null)};
  pointer-events: ${(props) => (props.isBlurred ? 'none' : 'auto')};

  // Transparent scrollbar
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
`;

const ChartScrollWrapper = styled('div')`
  min-width: ${(props) => props.minWidth}px;
  height: 400px;
`;

const YAxisContainer = styled('div')`
  position: sticky;
  left: 0; 
  z-index: 1; 
  background-color: '#fff';
`;

const NoDataModal = styled('div')`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: auto;
  width: 400px;
  background-color: #FFFFFF;
  border-radius: 8px;
  box-shadow: ${BoxShadow};
  font-family: 'Nunito Sans';
  font-size: 19px;
  color: #424242;
  padding: 15px;
  text-align: center;
`;

const ModalTitle = styled('div')`
  font-weight: bold;
`;

const AssignedCompletedChart = ({
  exerciseData,
  bucketSize,
  rangeMax,
  rangeMin,
  selectedAthlete,
  isLoading,
  readyPrint,
  viewOnly,
}) => {
  const containerRef = useRef(null);
  const MIN_BAR_HEIGHT = 8;
  const CHART_HEIGHT = 400;

  const [isTransitioning, setIsTransitioning] = useState(false);

  const isSideBarExpanded = useSelector(
    (state) => state.sportsScience.data.isSideBarExpanded,
  );

  useEffect(() => {
    setIsTransitioning(true);
    const timeout = setTimeout(() => {
      setIsTransitioning(false);
    }, 100);

    return () => clearTimeout(timeout);
  }, [bucketSize, rangeMin, rangeMax]);

  const getActiveBuckets = () => {
    const showHundredPlus = rangeMax > 100;
    const upperLimit = showHundredPlus ? 100 : rangeMax;

    const baseBuckets = Array.from(
      { length: Math.ceil((upperLimit - rangeMin) / bucketSize) },
      (_, i) => {
        const start = rangeMin + i * bucketSize;
        return {
          key: `r${start}to${start + bucketSize}`,
          label: `${start}–${start + bucketSize}%`,
        };
      },
    );

    return showHundredPlus
      ? [...baseBuckets, { key: 'r100plus', label: '100%+' }]
      : baseBuckets;
  };

  const activeBuckets = getActiveBuckets();

  // Builds raw bar chart data for each date and intensity bucket
  const rawFormattedData = useMemo(() => {
    if (!exerciseData?.dates?.length || rangeMin == null || rangeMax == null) {
      return [{
        date: '',
        ...Object.fromEntries(
          activeBuckets.flatMap(({ key }) => [
            [`assigned_${key}_raw`, 0],
            [`completed_${key}_raw`, 0],
          ]),
        ),
      }];
    }

    return exerciseData.dates.map((day) => {
      const formattedDate = moment(day.date).format('MMM D');
      const athleteData = selectedAthlete?.id
        ? day.athleteData?.find((a) => a.id === selectedAthlete.id)
        : day.groupData;

      const bucketEntries = activeBuckets.reduce((acc, { key }) => {
        const assigned = athleteData?.assigned?.[key]?.tonnage ?? 0;
        const completed = athleteData?.completed?.[key]?.tonnage ?? 0;
        return {
          ...acc,
          [`assigned_${key}_raw`]: assigned,
          [`completed_${key}_raw`]: completed,
        };
      }, {});

      return {
        date: formattedDate,
        ...bucketEntries,
      };
    });
  }, [exerciseData, rangeMin, rangeMax, bucketSize, selectedAthlete, activeBuckets]);

  // Checks if there is any real data (not zero values) to display
  const hasData = useMemo(() => rawFormattedData.some(
    (entry) => Object.keys(entry).some((key) => key.endsWith('_raw') && entry[key] > 0),
  ), [rawFormattedData]);

  const hasDatesButNoValues = exerciseData?.dates?.length > 0 && !hasData;

  // Two blocks below get highest values from data and produce clean intervals for ticks
  const dynamicMaxY = useMemo(() => {
    if (!hasData) return 5000;

    const max = rawFormattedData.reduce((acc, entry) => {
      const values = Object.entries(entry)
        .filter(([key]) => key.endsWith('_raw') && typeof entry[key] === 'number')
        .map(([, val]) => val);
      return Math.max(acc, ...values);
    }, 0);

    return Math.min(100000, Math.max(500, max));
  }, [rawFormattedData, hasData]);

  const getYAxisTicks = (maxY) => {
    const maxTicks = 5;
    if (maxY === 0) return [0];

    const rawInterval = maxY / maxTicks;
    const base = 10 ** Math.floor(Math.log10(rawInterval));
    const ratio = rawInterval / base;

    let cleanInterval;
    if (ratio <= 1) {
      cleanInterval = 1 * base;
    } else if (ratio <= 2) {
      cleanInterval = 2 * base;
    } else if (ratio <= 5) {
      cleanInterval = 5 * base;
    } else {
      cleanInterval = 10 * base;
    }

    const tickCount = Math.ceil(maxY / cleanInterval) + 1;
    return Array.from({ length: tickCount }, (_, i) => i * cleanInterval);
  };

  const minBarValue = useMemo(() => {
    const scale = dynamicMaxY / CHART_HEIGHT;
    return scale * MIN_BAR_HEIGHT;
  }, [dynamicMaxY]);

  // Uses the raw data and then adds the min bar height from above to any zero values
  const MAX_BAR_VALUE = 100000;

  const formattedData = useMemo(() => rawFormattedData.map((entry) => {
    const newEntry = { date: entry.date };

    activeBuckets.forEach(({ key }) => {
      const assignedRaw = entry[`assigned_${key}_raw`] ?? 0;
      const completedRaw = entry[`completed_${key}_raw`] ?? 0;

      const assignedMax = assignedRaw === 0
        ? minBarValue
        : Math.max(minBarValue, Math.min(assignedRaw, MAX_BAR_VALUE));
      const completedMax = completedRaw === 0
        ? minBarValue
        : Math.max(minBarValue, Math.min(completedRaw, MAX_BAR_VALUE));

      newEntry[`assigned_${key}`] = assignedMax;
      newEntry[`completed_${key}`] = completedMax;
      newEntry[`assigned_${key}_raw`] = assignedRaw;
      newEntry[`completed_${key}_raw`] = completedRaw;
    });

    return newEntry;
  }), [rawFormattedData, dynamicMaxY, activeBuckets]);

  // Size bars dynamically based on total bar count
  const bucketCount = (rangeMax - rangeMin) / bucketSize;
  const totalBars = formattedData.length * bucketCount * 2;

  const getBarSize = (barCount, printMode) => {
    if (barCount <= 10) return 75;
    if (barCount <= 20) return 50;
    if (barCount <= 40) return 25;

    if (printMode) {
      const maxPrintableWidth = 1100;
      const minBarSize = 5;
      const maxBarSize = 25;
      const calculatedSize = Math.floor(maxPrintableWidth / barCount);
      return Math.max(minBarSize, Math.min(calculatedSize, maxBarSize));
    }

    return 35;
  };

  const barSize = getBarSize(totalBars, readyPrint);
  const yAxisWidth = 90;
  // This allows us to fit the custom Y-Axis and add space between bar groups
  const numBarsPerGroup = activeBuckets.length * 2;
  const baseChartWidth = formattedData.length * (barSize * numBarsPerGroup + 5);
  const minPrintWidth = 1100;

  const chartMinWidth = Math.max(
    baseChartWidth + yAxisWidth, readyPrint ? minPrintWidth : 0,
  );

  // Group and bar color logic for different buckets
  const baseColors = ['#FBCC53', '#8006F4', '#FC4A1A', '#7A431C', '#1ADAFC'];

  const barGradients = activeBuckets.flatMap(({ key }, index) => {
    const baseColor = baseColors[index % baseColors.length];
    return [
      <linearGradient key={`assigned-${key}`} id={`assigned-${key}`} x1='0' y1='1' x2='0' y2='0'>
        <stop offset='0%' stopColor={baseColor} stopOpacity={0.2} />
        <stop offset='100%' stopColor={baseColor} stopOpacity={0.6} />
      </linearGradient>,
      <linearGradient key={`completed-${key}`} id={`completed-${key}`} x1='0' y1='1' x2='0' y2='0'>
        <stop offset='0%' stopColor={baseColor} stopOpacity={0.6} />
        <stop offset='100%' stopColor={baseColor} stopOpacity={1} />
      </linearGradient>,
    ];
  });

  const barGroups = activeBuckets.flatMap(({ key, label }, index) => {
    const baseColor = baseColors[index % baseColors.length];
    return [
      <Bar
        key={`assigned-${key}`}
        dataKey={`assigned_${key}`}
        name={`Assigned ${label}`}
        fill={`url(#assigned-${key})`}
        barSize={barSize}
        radius={[5, 5, 0, 0]}
        isAnimationActive={false}
      />,
      <Bar
        key={`completed-${key}`}
        dataKey={`completed_${key}`}
        name={`Completed ${label}`}
        fill={`url(#completed-${key})`}
        barSize={barSize}
        radius={[5, 5, 0, 0]}
        stroke={baseColor}
        strokeWidth={0}
        isAnimationActive={false}
      />,
    ];
  });

  return (
    <>
      <ChartWrapper>
        <ExerciseHeader>
          <Text
            styledTextProps={{ fontSize: '18px', fontWeight: 600 }}
          >
            {exerciseData?.exerciseName}
          </Text>
        </ExerciseHeader>
        {isTransitioning ? (
          <ChartContainer isLoading={isLoading}>
            <Spinner />
          </ChartContainer>
        ) : (
          <>
            <FilterWrapper isBlurred={!hasData}>
              <CustomChartLegend
                rangeMin={rangeMin}
                rangeMax={rangeMax}
                bucketSize={bucketSize}
                baseColors={baseColors}
                activeBuckets={activeBuckets}
                showAssigned
              />
            </FilterWrapper>
            {/* This is a workaround to add a sticky Y-Axis that works with scroll */}
            <PrintWrapper isSideBarExpanded={isSideBarExpanded}>
              <FilterWrapper isBlurred={!hasData}>
                <YAxisContainer>
                  <BarChart
                    data={formattedData}
                    width={85}
                    height={400}
                    margin={{
                      top: 30, bottom: 50, right: 20, left: 0,
                    }}
                  >
                    <XAxis dataKey='date' width={0} />
                    <YAxis
                      width={yAxisWidth}
                      tickLine={false}
                      axisLine={false}
                      interval={0}
                      tick={{
                        fontSize: 16,
                        fontFamily: 'Nunito Sans',
                        fill: '#9E9E9E',
                      }}
                      label={{
                        value: 'Volume',
                        angle: -90,
                        position: 'insideLeft',
                        offset: 10,
                        dx: 10,
                        style: {
                          textAnchor: 'middle',
                          fontSize: 16,
                          fontFamily: 'Nunito Sans',
                          fill: '#9E9E9E',
                        },
                      }}
                      domain={[0, dynamicMaxY]}
                      ticks={getYAxisTicks(dynamicMaxY)}
                    />
                  </BarChart>
                </YAxisContainer>
                {/* This is the actual graph being displayed */}
                <ChartContainer ref={containerRef}>
                  <ChartScrollWrapper minWidth={chartMinWidth}>
                    {/* Width logic here is the key to print sizing */}
                    <ResponsiveContainer width={readyPrint ? chartMinWidth : '100%'} height='100%'>
                      <BarChart
                        data={formattedData}
                        barGap={2}
                        barCategoryGap={15}
                        margin={{
                          top: 30, bottom: 50, right: 20, left: 0,
                        }}
                      >
                        <defs>{barGradients}</defs>
                        <CartesianGrid vertical={false} stroke='#E0E0E0' strokeDasharray='0' />
                        <XAxis
                          dataKey='date'
                          angle={-45}
                          textAnchor='end'
                          dy={10}
                          tick={{
                            fontSize: 16,
                            fontFamily: 'Nunito Sans',
                            fill: '#9E9E9E',
                          }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          hide={false}
                          width={0}
                          tickLine={false}
                          axisLine={false}
                          interval={0}
                          domain={[0, dynamicMaxY]}
                          ticks={getYAxisTicks(dynamicMaxY)}
                        />
                        {!readyPrint && hasData && (
                          <Tooltip
                            content={(props) => (
                              <CustomChartToolTip
                                {...props}
                                buckets={activeBuckets}
                                barSize={barSize}
                                chartWidth={chartMinWidth}
                                showAssigned
                              />
                            )}
                            position={{ x: 0, y: 0 }}
                          />
                        )}
                        {barGroups}
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartScrollWrapper>
                </ChartContainer>
              </FilterWrapper>
            </PrintWrapper>
            {!isLoading && (
              exerciseData?.dates?.length === 0 || hasDatesButNoValues) && (
              <NoDataModal>
                <ModalTitle>
                  {exerciseData?.dates?.length === 0
                    ? 'No Data Available'
                    : 'No Data for Selected Range'}
                </ModalTitle>
                <Text
                  styledTextProps={{
                    fontWeight: 300,
                    fontSize: '19px',
                    color: '#424242',
                    textAlign: 'center',
                  }}
                >
                  {exerciseData?.dates?.length === 0
                    ? (
                      <>
                        There is no data available for
                        {' '}
                        <strong>{exerciseData.exerciseName}</strong>
                        , please adjust your selection above.
                      </>
                    )
                    : (
                      <>
                        There is no data available for
                        {' '}
                        <strong>{exerciseData.exerciseName}</strong>
                        {' '}
                        within the selected intensity buckets.
                        {' '}
                        {viewOnly
                          ? ' Please select another athlete.'
                          : ' Please adjust your filter selection.'}
                      </>
                    )}
                </Text>
              </NoDataModal>
            )}
          </>
        )}
      </ChartWrapper>
    </>
  );
};

AssignedCompletedChart.propTypes = {
  exerciseData: PropTypes.shape({
    exerciseName: PropTypes.string,
    dates: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string,
        groupData: PropTypes.shape({
          assigned: PropTypes.shape({
            tonnage: PropTypes.number,
          }),
          completed: PropTypes.shape({
            tonnage: PropTypes.number,
          }),
        }),
      }),
    ),
  }),
  bucketSize: PropTypes.number.isRequired,
  rangeMax: PropTypes.number.isRequired,
  rangeMin: PropTypes.number.isRequired,
  selectedAthlete: PropTypes.shape({
    id: PropTypes.number,
  }),
  isLoading: PropTypes.bool.isRequired,
  readyPrint: PropTypes.bool,
  viewOnly: PropTypes.bool,
};

AssignedCompletedChart.defaultProps = {
  exerciseData: {
    exerciseName: '',
    dates: [],
  },
  selectedAthlete: null,
  readyPrint: false,
  viewOnly: false,
};

export default AssignedCompletedChart;
